import React, { useEffect, useState } from 'react';

import { Button, Grid, ListItem, Typography, ListSubheader, Accordion, AccordionSummary, CircularProgress, Divider, makeStyles} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorIcon from '@material-ui/icons/Error';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import { SermonGeniusResultsList } from './components/sermonGeniusResultsList';
import { UnpaidDrawer } from './components/unpaidDrawer';

import { BuilderPreferences } from '../../../entities/BuilderPreferences';
import { BuilderAsset } from '../../../entities/BuilderAsset';
import { SermonGeniusSearchItem, GeniusSearchService, GeniusSearchRequest } from '../../../../src/services/GeniusSearchService';
import { SearchService } from '../../../services/SearchService';
import { EventTracking, SearchEntity } from '../../../../src/utilities/Tracking/EventTracking';
import KeyWordParser from "../../../utilities/KeyWordParser"
import { AddAssetAction } from '../../../entities/AddAssetAction';
import { useAuthenticatedUser } from '../../../utilities/useAuthenticatedUser';
import { useBuilderDrawer } from './useBuilderDrawer';
import { ScriptureReferenceParser } from '../../../utilities/ScriptureReferenceParser';

const useStyles = makeStyles((theme) => ({
    title: {
        paddingTop: 10,
        paddingBottom: 7,
        textAlign: 'left',
        fontSize: 22
    },
    subtitle: {
        paddingTop: theme.spacing(0),
        textAlign: 'center',
        paddingBottom: 10,
        fontSize: 19
    },
    closeIcon: {
        float: 'right'
    },
    placeHolderIcon: {
        marginTop: "30px",
        width: "50px",
        height: "50px",
        color: "#CCC"
    },
    placeHolderText: {
        fontFamily: "Roboto, Helvetica",
        fontSize: "16px",
        marginTop: "12px",
        color: "#5f6368",
        textAlign: 'center'
    },
    panel: {
        boxShadow: 'none',
        margin: 0
    },
    panelSummary: {
        color: '#fff',
        textSize: '15px',
    },
    illustrationTitle: {
        color: '#fff',
        border: '1px solid #fff',
        backgroundColor: '#961617',
        padding: '0 24px',
        margin: 0
    },
    sermonTitle: {
        color: '#fff',
        border: '1px solid #fff',
        backgroundColor: '#f26321',
        padding: '0 24px',
        margin: 0
    },
    bibleTitle: {
        color: '#fff',
        border: '1px solid #fff',
        backgroundColor: '#027172',
        padding: '0 24px',
        margin: 0
    },
}));

interface Props {
    asset: BuilderAsset;
    lastSearched: string;
    onDrawerToggle: (open: boolean) => void;
    geniusSearchResults: (verses:SermonGeniusSearchItem[], illustrations:SermonGeniusSearchItem[], sermons:SermonGeniusSearchItem[], translationId:string) => void;
    geniusSearchCounter?: Date;
    removeSermonResult: (id:string) => void;
    removeIllustrationResult: (id:string) => void;
    onCopyVerse: (verseContent: string) => void;
    onBibleVerseSelected: (scriptureReference: string) => void;
    preferences: BuilderPreferences;
    onIllustrationSelected: (id: string) => void;
    onCopyIllustration: (sermonContent: string) => void;
    onSermonSelected: (id: string) => void;
    onCopySermon: (sermonContent: string) => void;
    verses: SermonGeniusSearchItem[];
    illustrations: SermonGeniusSearchItem[];
    sermons: SermonGeniusSearchItem[];
    onError: (message:string) => void;
    translationId?: string;
}

const currentLocation = "Genius Drawer Interaction"

export function GeniusSearch(props:Props) {
    const classes = useStyles();
    const drawerContext = useBuilderDrawer();
    const {asset, sermons, illustrations, verses, preferences} = props;
    const user = useAuthenticatedUser();

    const [illustrationServerFailed, setIllustrationServerFailed] = useState(false);
    const [sermonServerFailed, setSermonServerFailed] = useState(false);
    const [bibleVerseServerFailed, setBibleVerseServerFailed] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [canRefresh, setCanRefresh] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        drawerContext.dismissGeniusBadge();
        if (user.makerSubscription){
            if( sermons.length === 0 || illustrations.length === 0 || verses.length === 0 ) {
                searchGenius();
            }
        }
    }, [sermons, illustrations, verses]);

    useEffect(() => {
        if (props.translationId !== props.preferences.bibleTranslationId && user.makerSubscription)
            setCanRefresh(true);
    }, [props.translationId, props.preferences])

    useEffect(() => {
        if(props.asset && props.geniusSearchCounter && user.makerSubscription)
            if(props.asset.dateModified.getTime() > props.geniusSearchCounter.getTime() && !canRefresh)
                setCanRefresh(true);
    }, [props.asset, props.geniusSearchCounter])

    const updateLoadingText = (text:string, time:number) => setTimeout(() => setLoadingText(text), time);

    const onDrawerToggle = (open: boolean) => {setOpen(open); props.onDrawerToggle(open)}

    const searchGenius = async() => {

        if (!asset) return;

        displayLoadingState();

        const title = asset.name;
        const keywords = await getKeywords(asset.text);
        const scriptureRefs = await getScriptureRefs(asset.text);
        const translationId = preferences.bibleTranslationId;

        const request:GeniusSearchRequest = {
            title: title, 
            keywords: keywords, 
            scriptureRefs: scriptureRefs, 
            translationId: translationId
        };
            
        const responses = await Promise.all([
            GeniusSearchService.findBibleVerses(request),
            GeniusSearchService.findSermonIllustrations(request),
            GeniusSearchService.findSermons(request)
        ]);
        
        const [searchVerseResult, searchSermonIllustrationResult, searchSermonResult] = responses

        let verseResults: SermonGeniusSearchItem[] = [];

        if(searchVerseResult.error){
            setBibleVerseServerFailed(true);
            setCanRefresh(true);
        }
        if(searchVerseResult.success){
            verseResults = searchVerseResult.data;
            setBibleVerseServerFailed(false);
        }
        
        let sermonIllustrationResults: SermonGeniusSearchItem[] = [];

        if(searchSermonIllustrationResult.error){
            setIllustrationServerFailed(true);
            setCanRefresh(true);
        }
        if(searchSermonIllustrationResult.success) {
            sermonIllustrationResults = searchSermonIllustrationResult.data;
            setIllustrationServerFailed(false);
        }

        let sermonResults: SermonGeniusSearchItem[] = [];

        if(searchSermonResult.error){
            setSermonServerFailed(true);
            setCanRefresh(true);
        }
        if (searchSermonResult.success){
            sermonResults = searchSermonResult.data;
            setSermonServerFailed(false);
        }
        
        props.geniusSearchResults(verseResults, sermonIllustrationResults, sermonResults, translationId ? translationId : "NIV" );
    }

    const getKeywords = async(text:string) => {
        if (text.length === 0){
            return;
        }
        
        const stopWords = ["god", "jesus", "bible"]
        const foundKeywords = await KeyWordParser.parseKeywords(text, stopWords);

        return foundKeywords;
    }

    const getScriptureRefs = async(text:string) => {
        if (text.length === 0){
            return;
        }

        return ScriptureReferenceParser.parse(text)
    }

    const displayLoadingState = () => {
        setLoading(true);
        setLoadingText("Searching 31,102 Bible Verses");
        updateLoadingText("Searching 181,667 Sermons", 1500);
        updateLoadingText("Searching 49,363 illustrations", 3000);
        setTimeout(() => {
            setLoading(false)
            setCanRefresh(false)
        }, 4500);
    }

    const removeSermonResult = async (id: string) => {
        setDisabled(true);
        const response = await GeniusSearchService.blockSermon(id);
        setDisabled(false);
        
        if(response.error) return props.onError('Remove Sermon failed. Please try again.');

        props.removeSermonResult(id);
    }

    const removeSermonIllustrationResult = async(id: string) => {
        setDisabled(true);        
        const response = await GeniusSearchService.blockSermonIllustration(id);
        setDisabled(false);

        if(response.error) return props.onError('Remove Sermon Illustration failed. Please try again.');

        props.removeIllustrationResult(id)
    }

    // Bible Search Functions
    const onCopyVerse = async(selectedVerse: SermonGeniusSearchItem) => {
        // We don't want any verse numbers or the encaspulating html to be on the editor. 
        let formattedVerse = `${selectedVerse.description.replace(/<sup>\d+<\/sup>/g, '')}\n${selectedVerse.title} - ${props.preferences.bibleTranslationId}`;
        props.onCopyVerse(formattedVerse);
        EventTracking.Builder.onCopy(SearchEntity.Genius, currentLocation);
    }

    // Sermon Search functions
    const onCopySermon = async(selectedSermon: SermonGeniusSearchItem) => {
        let sermon = await SearchService.getSermon(selectedSermon.id!);
        let bodyNoHtml = sermon.body.replace(/<\/p>/g, "\n\n").replace(/<(?:.|\n)*?>/gm, '');
        props.onCopySermon(bodyNoHtml);
        EventTracking.Builder.onCopy(SearchEntity.Genius, currentLocation);
    }

    
    const onCopyIllustration = async(selectedIllustration: SermonGeniusSearchItem) => {
        let illustration = await SearchService.getIllustration(selectedIllustration.id!);
        let illustrationText = illustration.body.replace(/<\/p>/g, "\n").replace(/<(?:.|\n)*?>/gm, '');
        props.onCopyIllustration(illustrationText);
        EventTracking.Builder.onCopy(SearchEntity.Genius, currentLocation);
    }

    return (
        <>
            <ListSubheader className={classes.title}>
                    Sermon Genius
                <IconButton onClick={() => onDrawerToggle(false)} className={classes.closeIcon}>
                    <CloseIcon />
                </IconButton>
            </ListSubheader>
            <Divider />
            {   loading
                ? <LoadingState loadingText={loadingText} />
                : !user.makerSubscription
                ? <UnpaidDrawer
                    title="Sermon Genius"
                    listText={[
                        "Intelligent sermon idea engine", "Personalized manuscript analysis",
                        "Near-instant results from 250k sources", "Ideas from other pulpit-tested sermons",
                        "Suggested illustrations and stories", "Related Scripture passages"
                    ]} 
                    upgradeAction={AddAssetAction.Genius}
                    upgradePath={"Genius"} 
                    color='#2196F3'
                    displayNumber={15}
                />
                :   
                <>
                    <Grid container alignItems='center' alignContent='center' justifyContent='center' className={classes.title}>
                        { canRefresh
                        ?
                        <Button size="large"
                                variant="text"
                                color="primary" onClick={() => searchGenius()} style={{ textAlign:"center" }}>
                            <RefreshIcon/>
                            Refresh now
                        </Button>
                        :
                        <>
                            <RefreshIcon fontSize="small" style={{ color: "#BBBBBB", marginRight: 10 }}/>
                            <Typography variant='subtitle1' style={{ fontSize: "0.8rem", color: "#BBBBBB"}}>
                                { props.lastSearched }
                            </Typography>
                        </>   
                        }                      
                    </Grid>
                    <Divider />
        
                    <div className='customScrollbar' style={{ overflowY: 'auto'}}>
                        <Accordion className={classes.panel} defaultExpanded={true}>
                            <AccordionSummary className={classes.bibleTitle} expandIcon={<ExpandMoreIcon className={classes.panelSummary}/>}>
                                <Typography className={classes.panelSummary}>Related Bible Verses ({ verses.length })</Typography>
                            </AccordionSummary>
                            { bibleVerseServerFailed
                                ? <ServerFailed message='Bible Search failed. Please try again.' /> 
                                : <SermonGeniusResultsList
                                    results={verses}
                                    buttonText="View Full Chapter"
                                    selected={props.onBibleVerseSelected}
                                    copy={onCopyVerse } />
                            }

                        </Accordion>
                        <Divider />


                            <Accordion className={classes.panel} defaultExpanded={true}>
                                <AccordionSummary className={classes.sermonTitle} expandIcon={<ExpandMoreIcon className={classes.panelSummary}/>}>
                                    <Typography className={classes.panelSummary}>Suggested Sermons ({ sermons.length > 5 ? 5 : sermons.length })</Typography>
                                </AccordionSummary>
                                { sermonServerFailed
                                    ? <ServerFailed message='Sermon Search failed. Please try again.' />
                                    : <SermonGeniusResultsList
                                        results={sermons}
                                        buttonText="View Full Sermon"
                                        selected={props.onSermonSelected}
                                        copy={onCopySermon}
                                        remove={removeSermonResult}
                                        disabled={disabled} />
                                }
                            </Accordion>
                            
                            <Divider />

                        <Accordion className={classes.panel} defaultExpanded={true}>
                            <AccordionSummary className={classes.illustrationTitle} expandIcon={<ExpandMoreIcon className={classes.panelSummary}/>}>
                                <Typography className={classes.panelSummary}>Suggested Illustrations ({ illustrations.length > 5 ? 5 : illustrations.length })</Typography>
                            </AccordionSummary>
                            { illustrationServerFailed
                                ? <ServerFailed message='Block Sermon Illustration failed. Please try again.' />
                                : <SermonGeniusResultsList 
                                results={illustrations}
                                buttonText="View Full Illustration"
                                selected={props.onIllustrationSelected}
                                copy={onCopyIllustration}
                                remove={removeSermonIllustrationResult}
                                disabled={disabled} />
                            }
                        </Accordion>
                    </div>
                </>
            }
        </>
    );
}

function ServerFailed(props:{ message: string }) {
    const classes = useStyles();
    return (
        <ListItem>
            <Grid container alignItems='center' alignContent='center' justifyContent='center'  className={classes.placeHolderText}>
                <ErrorIcon className={classes.placeHolderIcon} /><br />
                <ListSubheader className={classes.title}>
                    <Typography>{props.message}</Typography>
                </ListSubheader>
            </Grid>
        </ListItem>
    )
}

function LoadingState(props:{loadingText: string}){
    return (
        <div style={{ textAlign: 'center' }}>
            <CircularProgress style={{ marginTop: "50px" }} />
            <br />
            <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
                { props.loadingText }
            </Typography>
        </div>
    )
}